import { useEffect, useRef, useState } from "react";
import { Save, Upload, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Field } from "@/components/UI";

export default function CompanySettings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigInput = useRef(null);
  const stampInput = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    api.getCompany().then(setC).catch(() => toast("Failed to load", "error"));
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try { await api.updateCompany(c); toast("Saved", "success"); }
    catch (e) { toast(e?.response?.data?.detail || "Save failed", "error"); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", kind);
      await api.uploadCompanyAsset(fd);
      toast(`${kind} uploaded`, "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Upload failed", "error");
    }
  };

  if (!c) return <div className="muted text-sm">Loading…</div>;

  const set = (k, v) => setC({ ...c, [k]: v });

  return (
    <div data-testid="page-company" className="max-w-4xl space-y-5">
      <PageHeader
        icon={Building2}
        title="Company Settings"
        subtitle="Used on every generated PDF — keep these accurate"
        action={
          <button data-testid="save-company" onClick={save} disabled={saving} className="btn btn-primary">
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <div className="card card-padded">
        <h3 className="font-bold mb-4">Legal & Identity</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Legal name"><input data-testid="legal-name" className="input" value={c.legal_name} onChange={(e) => set("legal_name", e.target.value)} /></Field>
          <Field label="Director"><input className="input" value={c.director} onChange={(e) => set("director", e.target.value)} /></Field>
          <Field label="TIN"><input data-testid="tin" className="input" value={c.tin} onChange={(e) => set("tin", e.target.value)} /></Field>
          <Field label="URSB Reg."><input className="input" value={c.ursb} onChange={(e) => set("ursb", e.target.value)} /></Field>
          <Field label="NIN (Director)"><input className="input" value={c.nin} onChange={(e) => set("nin", e.target.value)} /></Field>
          <Field label="Website"><input className="input" value={c.website} onChange={(e) => set("website", e.target.value)} /></Field>
        </div>
      </div>

      <div className="card card-padded space-y-3">
        <h3 className="font-bold">Address & Contact</h3>
        <Field label="Address"><input className="input" value={c.address} onChange={(e) => set("address", e.target.value)} /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="P.O. Box"><input className="input" value={c.po_box} onChange={(e) => set("po_box", e.target.value)} /></Field>
          <Field label="Branch"><input className="input" value={c.branch} onChange={(e) => set("branch", e.target.value)} /></Field>
        </div>
        <Field label="Phones (comma-separated)">
          <input
            className="input"
            value={(c.phones || []).join(", ")}
            onChange={(e) => set("phones", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
        </Field>
        <Field label="Emails (comma-separated)">
          <input
            className="input"
            value={(c.emails || []).join(", ")}
            onChange={(e) => set("emails", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          />
        </Field>
      </div>

      <div className="card card-padded">
        <h3 className="font-bold mb-4">Banking</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Bank name"><input className="input" value={c.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></Field>
          <Field label="Branch"><input className="input" value={c.bank_branch} onChange={(e) => set("bank_branch", e.target.value)} /></Field>
          <Field label="Account name"><input className="input" value={c.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} /></Field>
          <Field label="Account No"><input className="input" value={c.bank_account_no} onChange={(e) => set("bank_account_no", e.target.value)} /></Field>
          <Field label="SWIFT / BIC"><input className="input" value={c.bank_swift} onChange={(e) => set("bank_swift", e.target.value)} /></Field>
          <Field label="Country"><input className="input" value={c.country} onChange={(e) => set("country", e.target.value)} /></Field>
        </div>
      </div>

      <div className="card card-padded">
        <h3 className="font-bold mb-1">Signature & Stamp</h3>
        <p className="text-xs muted mb-4">
          PNG with transparent background preferred. Stamp will appear larger than the signature on PDFs.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <button onClick={() => sigInput.current?.click()} className="btn btn-ghost w-full justify-center py-6 border-dashed">
            <Upload size={14} /> Upload signature
          </button>
          <input ref={sigInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("signature", e.target.files?.[0])} />
          <button onClick={() => stampInput.current?.click()} className="btn btn-ghost w-full justify-center py-6 border-dashed">
            <Upload size={14} /> Upload stamp
          </button>
          <input ref={stampInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("stamp", e.target.files?.[0])} />
        </div>
      </div>

      <PermissionsCard toast={toast} />
    </div>
  );
}

function PermissionsCard({ toast }) {
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getPermissions().then(setPerms).catch(() => {});
  }, []);
  if (!perms) return null;
  const ACTIONS = [
    { key: "edit", label: "Create & edit documents" },
    { key: "email", label: "Email documents to clients" },
    { key: "delete", label: "Delete documents" },
  ];
  const toggle = (role, key) =>
    setPerms({ ...perms, [role]: { ...perms[role], [key]: !perms[role][key] } });
  const savePerms = async () => {
    setSaving(true);
    try {
      const updated = await api.updatePermissions(perms);
      setPerms(updated);
      toast("Permissions saved", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "Save failed (admin only)", "error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="card card-padded" data-testid="permissions-card">
      <h3 className="font-bold mb-1">Role permissions — Quotations, Job Cards & SDNs</h3>
      <p className="text-xs muted mb-4">
        Everyone on the team can always <b>view</b> all documents. Untick to make a role view-only
        for that action. Admins always have full access.
      </p>
      <div className="overflow-x-auto">
        <table className="text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider muted">
              <th className="pr-8 py-2">Permission</th>
              <th className="pr-8 py-2">Manager</th>
              <th className="pr-8 py-2">Member</th>
            </tr>
          </thead>
          <tbody>
            {ACTIONS.map((a) => (
              <tr key={a.key} className="border-t border-slate-50">
                <td className="pr-8 py-2.5">{a.label}</td>
                {["manager", "member"].map((role) => (
                  <td key={role} className="pr-8 py-2.5">
                    <input
                      type="checkbox"
                      data-testid={`perm-${role}-${a.key}`}
                      checked={!!perms[role]?.[a.key]}
                      onChange={() => toggle(role, a.key)}
                      className="w-4 h-4 accent-[#0e6939] cursor-pointer"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button data-testid="perm-save" onClick={savePerms} disabled={saving} className="btn btn-primary mt-4">
        <Save size={14} /> {saving ? "Saving…" : "Save permissions"}
      </button>
    </div>
  );
}
